import { networkToTopicIdentifiersMap } from 'hb-react/shared/constants/pageIdentifiers';
import { type PageIdentifier } from 'hb-react/shared/constants/pageIdentifiers';
import { type Menu } from 'hb-react/types';

import { type PageNames } from './types';

export const getTopicPageIdentifier = (
  identifier: PageIdentifier,
): PageIdentifier => networkToTopicIdentifiersMap[identifier];

export const getNameFromMenu = (menu, identifier): string | undefined => {
  if (!menu || !identifier) {
    return undefined;
  }

  for (const item of menu) {
    if (item.identifier === identifier) {
      return item.menu_name;
    }

    if (item.menu?.identifier === identifier) {
      return item.menu.menu_name;
    }

    if (item.submenus) {
      const name = getNameFromMenu(item.submenus, identifier);

      if (name) {
        return name;
      }
    }
  }

  return undefined;
};

/**
 * Helper function to get the names of the pages from the global or topic menu.
 *
 * @param opts {Object} Options object.
 * @param [opts.menu] {Menu} The menu to get the names from.
 * @param [opts.identifiers] {Array<PageIdentifier>} The identifiers of the pages.
 * @param [opts.isTopic] {boolean} Whether the menu is a topic menu.
 * @returns {Object} The names of the pages and the identifiers to fetch.
 */
export const getPageNamesFromMenu = ({
  availableNames = {},
  identifiers = [],
  isTopic,
  menu = [],
}: {
  availableNames?: PageNames;
  identifiers?: Array<PageIdentifier>;
  isTopic?: boolean;
  menu?: Menu;
}) => {
  if (!identifiers.length) {
    return {
      pageNames: {},
      notFound: [],
    };
  }

  const notFound: Array<PageIdentifier> = [];

  // Get the names of the pages from the menu. If the page is not found in
  // the menu, add it to the names to fetch.
  const pageNames = identifiers.reduce<PageNames>((acc, identifier) => {
    const normalizedIdentifier = isTopic
      ? getTopicPageIdentifier(identifier)
      : identifier;

    let pageName = getNameFromMenu(menu, normalizedIdentifier);
    pageName ??= availableNames[normalizedIdentifier];

    if (!pageName) {
      notFound.push(normalizedIdentifier);
    }

    acc[normalizedIdentifier] = pageName;

    return acc;
  }, {});

  return {
    pageNames,
    notFound,
  };
};

export const fillUnknownWithEllipsisStrings = (
  pageNames: PageNames,
): PageNames =>
  Object.keys(pageNames).reduce<PageNames>((acc, identifier) => {
    acc[identifier] = pageNames[identifier] || '...';
    return acc;
  }, {});

export const fillUnknownWithDefaultNames = (
  pageNames: PageNames,
  defaultNames: PageNames,
): PageNames =>
  Object.keys(pageNames).reduce<PageNames>((acc, identifier) => {
    acc[identifier] = pageNames[identifier] || defaultNames[identifier];
    return acc;
  }, {});
